import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import { getApiErrorMessage } from '../utils/errors';

/**
 * Asks a signed-in user to verify their email address, and can resend the link.
 *
 * Hidden for Facebook sign-ups without an email: their placeholder address
 * (facebook_<id>@facebook.com) can't receive mail.
 */
const EmailVerificationBanner: React.FC = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState('');
  const [dismissed, setDismissed] = useState(false);

  if (
    !user ||
    user.email_verified !== false ||
    user.email.endsWith('@facebook.com') ||
    pathname === '/verify-email' ||
    dismissed
  ) {
    return null;
  }

  const handleResend = async () => {
    setSending(true);
    setMessage('');

    try {
      const response = await authApi.requestEmailVerification();
      setSent(true);
      setMessage(response.message);
    } catch (err) {
      setMessage(getApiErrorMessage(err, 'Failed to send a new link. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#f8f6f2] border-b border-[#c6a664] px-4 py-3" role="status">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 font-['Ovo',_serif] text-[14px] text-[#1f1f1f]">
        <p className="flex-1">
          {message ||
            `Please verify your email address, ${user.email}. Check your inbox for the link we sent.`}
        </p>
        <div className="flex items-center gap-4">
          {!sent && (
            <button
              type="button"
              onClick={handleResend}
              disabled={sending}
              className="font-semibold text-[#c6a664] underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending...' : 'Resend link'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="text-[#767472] hover:text-[#1f1f1f]"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
